/**
 * SFU backend for voice — extracted verbatim from voiceMedia's controller so
 * the god file stays focused on snapshot orchestration. This owns the
 * mediasoup-client device/transports/producers/consumers and the SFU signalling
 * protocol; it reaches back into the controller only through the narrow
 * {@link SfuHost} interface (local-stream acquisition, snapshot writes, logging).
 *
 * Pure move: the logic is unchanged from the in-class version. Behaviour is
 * pinned by voiceMedia.sfu.test.ts + voiceMedia.media.test.ts.
 */
import type {
  Consumer,
  Device,
  DtlsParameters,
  MediaKind,
  Producer,
  RtpCapabilities,
  RtpParameters,
  Transport,
} from 'mediasoup-client/types';
import { getSocket } from '@/lib/socket';
import type { AudioProcessingMode } from '@/lib/voice/voiceMediaHelpers';
import type { RemoteVoiceStream, SfuMediaTag } from './voiceMedia';
import { summarizeRtcStats, type VoiceDiagnostics } from './voiceDiagnostics';

interface SfuTransportParams {
  id: string;
  iceParameters: unknown;
  iceCandidates: unknown;
  dtlsParameters: unknown;
}

interface SfuProducerSummary {
  producerId: string;
  userId: string;
  kind: MediaKind;
  mediaTag?: SfuMediaTag;
}

interface SfuConsumerParams {
  id: string;
  producerId: string;
  userId: string;
  kind: MediaKind;
  mediaTag?: SfuMediaTag;
  rtpParameters: RtpParameters;
}

interface SfuRemoteConsumer {
  consumer: Consumer;
  stream: MediaStream;
  userId: string;
  producerId: string;
  mediaTag: SfuMediaTag;
}

/** What the SFU session needs from the voice controller. Implemented with
 *  closures so the controller's internals stay private. */
export interface SfuHost {
  ensureLocalStream(): Promise<MediaStream>;
  getProductionTrack(): Promise<MediaStreamTrack | null>;
  getActiveChannelId(): string | null;
  getMyUserId(): string | null;
  /** /api/rtc/config has answered — voice media may connect. */
  isMediaReady(): boolean;
  getAudioProcessingMode(): AudioProcessingMode;
  setRemoteStreams(streams: RemoteVoiceStream[]): void;
  setError(error: string | null): void;
  /** True while the call is getting its connection back (ICE restart after
   *  the grace, a rebuild, or a rejoin after the socket dropped). */
  setReconnecting(reconnecting: boolean): void;
  recordDebug(event: string): void;
  stopLocalScreenStream(): void;
}

/** How long a «disconnected» transport may heal by itself before ICE restarts. */
const ICE_GRACE_MS = 3_000;
/** How long an ICE restart gets to reconnect before the session is rebuilt. */
const ICE_CHECK_MS = 6_000;
/** At most one ICE restart per transport in this window; after that, rebuild. */
const ICE_RETRY_MS = 15_000;

/**
 * One transport's recovery. `grace`: waiting to see if it heals by itself;
 * `restarting`: asked the server for new ICE parameters; `checking`: restarted,
 * waiting for it to connect. While a cycle runs, repeated «disconnected» /
 * «failed» events change nothing — only «connected» or the cycle's own
 * outcome end it.
 */
/** Read fresh each time: the state moves under us across awaits and timers. */
function isConnected(transport: Transport): boolean {
  return transport.connectionState === 'connected';
}

interface IceRecovery {
  phase: 'idle' | 'grace' | 'restarting' | 'checking';
  timer: number | null;
  restartedAt: number;
  /** Bumped when a cycle ends or a new one starts: an answer that comes back
   *  for an older generation belongs to a cycle that is over. */
  gen: number;
}

export class VoiceSfuSession {
  private sfuDevice: Device | null = null;
  private sfuSendTransport: Transport | null = null;
  private sfuRecvTransport: Transport | null = null;
  private sfuRecvTransportPromise: Promise<Transport> | null = null;
  private sfuAudioProducer: Producer | null = null;
  private sfuVideoProducer: Producer | null = null;
  private sfuScreenProducer: Producer | null = null;
  private sfuScreenAudioProducer: Producer | null = null;
  private sfuRemoteConsumers = new Map<string, SfuRemoteConsumer>();
  private sfuConsumingProducerIds = new Set<string>();
  private sfuStartPromise: Promise<void> | null = null;
  /**
   * Socket the live SFU session was built on. The server destroys our peer
   * (transports, producers, consumers) the instant that socket drops, so after
   * a reconnect every mediasoup object we hold is a corpse that still reports
   * `live` — which is why `startSfu` used to short-circuit and everyone sat in
   * a silent room until ICE eventually failed. Comparing socket ids makes the
   * session self-healing from its ONE entry point (startSfu), serialized by
   * `sfuStartPromise`; an external "recover now" caller raced the normal join
   * and could tear a healthy session down mid-setup.
   */
  private sessionSocketId: string | null = null;
  private sfuSignalBound = false;
  private sfuReconnectTimer: number | null = null;
  private sfuReconnectAttempts = 0;
  private closingSfu = false;
  /** Per transport: where its recovery stands (one cycle at a time). */
  private sfuIceRecovery = new Map<string, IceRecovery>();
  /**
   * The socket dropped and came back; the rejoin has not answered yet whether
   * the server kept our media session for the new socket (voiceResume). Until
   * it does, nothing is rebuilt and no ICE restart is sent — the server would
   * not know the transports under the new socket yet.
   */
  private resumePending = false;
  private reconnecting = false;

  constructor(private host: SfuHost) {}

  async startSfu(channelId: string): Promise<void> {
    const currentSocketId = getSocket().id ?? null;
    const staleSession =
      this.sessionSocketId !== null &&
      currentSocketId !== null &&
      this.sessionSocketId !== currentSocketId &&
      // NEVER tear down a session that is mid-setup: connectSfu stamps the
      // socket id up front, so an in-flight start already carries the old id
      // and would be dismantled underneath itself (the exact race that made
      // the previous, externally-triggered recovery dangerous). Let it finish —
      // the next startSfu sees the stale stamp and rebuilds cleanly.
      this.sfuStartPromise === null;
    // The rejoin decides whether this session lives on (finishResume).
    if (staleSession && this.resumePending) return;
    if (staleSession) {
      // Reconnected: drop the dead session so the code below rebuilds it (and
      // refreshSfuProducers re-consumes everyone) instead of trusting corpses.
      this.host.recordDebug(`sfu:stale-session:${this.sessionSocketId}->${currentSocketId}`);
      this.closeSfu({ clearReconnect: false });
    } else if (this.sfuAudioProducer && this.sfuAudioProducer.track?.readyState === 'live') {
      return;
    }
    if (!this.sfuStartPromise) {
      this.sfuStartPromise = this.connectSfu(channelId).finally(() => {
        this.sfuStartPromise = null;
      });
    }
    await this.sfuStartPromise;
    this.sfuReconnectAttempts = 0;
  }

  private async connectSfu(channelId: string): Promise<void> {
    this.bindSfuSignals();
    this.sessionSocketId = getSocket().id ?? null;
    // ensureLocalStream now returns an empty MediaStream when mic is
    // denied; the audio producer is skipped below so the user joins as
    // a listener. SendTransport is still set up so screen-share works.
    await this.host.ensureLocalStream();
    const audioTrack = await this.host.getProductionTrack();

    const device = await this.ensureSfuDevice(channelId);
    const sendTransport = await this.ensureSfuSendTransport(channelId, device);

    if (audioTrack && device.canProduce('audio')) {
      this.sfuAudioProducer = await sendTransport.produce({
        track: audioTrack,
        codecOptions: {
          opusStereo: this.host.getAudioProcessingMode() === 'studio',
          opusFec: true,
          opusDtx: this.host.getAudioProcessingMode() !== 'studio',
        },
        appData: { mediaTag: 'microphone' },
      });
      this.sfuAudioProducer.observer.on('close', () => {
        this.sfuAudioProducer = null;
      });
      this.host.recordDebug(`sfu:produce:${this.sfuAudioProducer.id}`);
    } else {
      this.host.recordDebug('sfu:listener-mode (no mic)');
    }

    await this.refreshSfuProducers(channelId);
  }

  async produceSfuVideo(channelId: string, track: MediaStreamTrack): Promise<void> {
    const existing = this.sfuVideoProducer;
    if (existing?.track?.id === track.id && existing.track.readyState === 'live') return;
    await this.closeSfuVideoProducer(channelId);
    const device = await this.ensureSfuDevice(channelId);
    if (!device.canProduce('video')) {
      throw new Error('sfu_video_not_supported');
    }
    const sendTransport = await this.ensureSfuSendTransport(channelId, device);
    this.sfuVideoProducer = await sendTransport.produce({
      track,
      encodings: [{ maxBitrate: 900_000, scaleResolutionDownBy: 1 }],
      appData: { mediaTag: 'camera' },
    });
    this.sfuVideoProducer.observer.on('close', () => {
      this.sfuVideoProducer = null;
    });
    this.host.recordDebug(`sfu:produce-video:${this.sfuVideoProducer.id}`);
  }

  async produceSfuScreen(
    channelId: string,
    track: MediaStreamTrack,
    maxBitrate: number,
  ): Promise<void> {
    const existing = this.sfuScreenProducer;
    if (existing?.track?.id === track.id && existing.track.readyState === 'live') return;
    await this.closeSfuScreenProducer(channelId);
    const device = await this.ensureSfuDevice(channelId);
    if (!device.canProduce('video')) {
      throw new Error('sfu_video_not_supported');
    }
    const sendTransport = await this.ensureSfuSendTransport(channelId, device);
    this.sfuScreenProducer = await sendTransport.produce({
      track,
      // Bitrate scaled to the chosen resolution + fps (see screenMaxBitrate):
      // a flat 4 Mbps starved 1440p/60. Full resolution (no downscale) — drop
      // frame rate, not resolution, under congestion (contentHint:'detail').
      encodings: [{ maxBitrate, scaleResolutionDownBy: 1 }],
      // Start at ~half the target (kbps) so high tiers ramp up quickly instead
      // of crawling from a 1 Mbps default; capped so we never overshoot.
      codecOptions: { videoGoogleStartBitrate: Math.min(2500, Math.round(maxBitrate / 2000)) },
      appData: { mediaTag: 'screen' },
    });
    this.sfuScreenProducer.observer.on('close', () => {
      this.sfuScreenProducer = null;
    });
    this.host.recordDebug(`sfu:produce-screen:${this.sfuScreenProducer.id}`);
  }

  async produceSfuScreenAudio(channelId: string, track: MediaStreamTrack): Promise<void> {
    const existing = this.sfuScreenAudioProducer;
    if (existing?.track?.id === track.id && existing.track.readyState === 'live') return;
    await this.closeSfuScreenAudioProducer(channelId);
    const device = await this.ensureSfuDevice(channelId);
    if (!device.canProduce('audio')) {
      throw new Error('sfu_audio_not_supported');
    }
    const sendTransport = await this.ensureSfuSendTransport(channelId, device);
    this.sfuScreenAudioProducer = await sendTransport.produce({
      track,
      // Shared system audio is usually music/stereo content — keep both
      // channels, FEC on for resilience, DTX off (continuous signal).
      codecOptions: { opusStereo: true, opusFec: true, opusDtx: false },
      appData: { mediaTag: 'screen-audio' },
    });
    this.sfuScreenAudioProducer.observer.on('close', () => {
      this.sfuScreenAudioProducer = null;
    });
    this.host.recordDebug(`sfu:produce-screen-audio:${this.sfuScreenAudioProducer.id}`);
  }

  /** Swap the live screen-audio producer's track without renegotiation —
   *  used by the silence watchdog to fall back from a per-app capture that
   *  never delivered sound onto the system capture. No-op when idle. */
  async replaceSfuScreenAudioTrack(track: MediaStreamTrack): Promise<void> {
    const producer = this.sfuScreenAudioProducer;
    if (!producer || producer.closed) throw new Error('sfu_screen_audio_not_producing');
    await producer.replaceTrack({ track });
    this.host.recordDebug(`sfu:screen-audio:replace-track:${producer.id}`);
  }

  /** Retarget the screen producer's encoder bitrate mid-share (live quality
   *  change). Uses the underlying RTCRtpSender — no producer restart. */
  async updateSfuScreenMaxBitrate(maxBitrate: number): Promise<void> {
    const sender = this.sfuScreenProducer?.rtpSender;
    if (!sender) return;
    const params = sender.getParameters();
    const encoding = params.encodings?.[0];
    if (encoding) {
      encoding.maxBitrate = maxBitrate;
    } else {
      params.encodings = [{ maxBitrate }];
    }
    await sender.setParameters(params);
  }

  /** Stats of the screen video sender (auto-quality sampling). Null while not
   *  producing a screen track. */
  async getSfuScreenSenderStats(): Promise<RTCStatsReport | null> {
    const sender = this.sfuScreenProducer?.rtpSender;
    if (!sender) return null;
    return sender.getStats();
  }

  private async ensureSfuDevice(channelId: string): Promise<Device> {
    if (this.sfuDevice?.loaded) return this.sfuDevice;
    const { Device } = await import('mediasoup-client');
    const device = new Device();
    const { rtpCapabilities } = await this.sfuRequest<{ rtpCapabilities: RtpCapabilities }>(
      'voice:sfu:get-router-rtp-capabilities',
      { channelId },
    );
    await device.load({ routerRtpCapabilities: rtpCapabilities });
    this.sfuDevice = device;
    this.host.recordDebug('sfu:device:loaded');
    return device;
  }

  private async ensureSfuSendTransport(channelId: string, device: Device): Promise<Transport> {
    if (this.sfuSendTransport) return this.sfuSendTransport;
    const params = await this.sfuRequest<SfuTransportParams>('voice:sfu:create-transport', {
      channelId,
      direction: 'send',
    });
    // mediasoup transport params come from the SFU as opaque JSON; their
    // exact mediasoup-client shape is validated at runtime by the device.
    const transport = device.createSendTransport(params as never);
    transport.on('connect', ({ dtlsParameters }, callback, errback) => {
      this.sfuRequest('voice:sfu:connect-transport', {
        transportId: transport.id,
        dtlsParameters: dtlsParameters as DtlsParameters,
      })
        .then(() => callback())
        .catch((err) => errback(err instanceof Error ? err : new Error('sfu_connect_failed')));
    });
    transport.on('produce', ({ kind, rtpParameters, appData }, callback, errback) => {
      this.sfuRequest<{ id: string }>('voice:sfu:produce', {
        channelId,
        transportId: transport.id,
        kind,
        rtpParameters: rtpParameters as RtpParameters,
        mediaTag: (appData as { mediaTag?: unknown } | undefined)?.mediaTag,
      })
        .then(({ id }) => callback({ id }))
        .catch((err) => errback(err instanceof Error ? err : new Error('sfu_produce_failed')));
    });
    transport.observer.on('close', () => {
      if (this.sfuSendTransport === transport) this.sfuSendTransport = null;
    });
    transport.on('connectionstatechange', (state) => this.onSfuTransportState(transport, 'send', state));
    this.sfuSendTransport = transport;
    this.host.recordDebug(`sfu:send-transport:${transport.id}`);
    return transport;
  }

  private async ensureSfuRecvTransport(channelId: string, device: Device): Promise<Transport> {
    if (this.sfuRecvTransport) return this.sfuRecvTransport;
    if (this.sfuRecvTransportPromise) return this.sfuRecvTransportPromise;
    this.sfuRecvTransportPromise = this.createSfuRecvTransport(channelId, device).finally(() => {
      this.sfuRecvTransportPromise = null;
    });
    return this.sfuRecvTransportPromise;
  }

  private async createSfuRecvTransport(channelId: string, device: Device): Promise<Transport> {
    const params = await this.sfuRequest<SfuTransportParams>('voice:sfu:create-transport', {
      channelId,
      direction: 'recv',
    });
    // mediasoup transport params come from the SFU as opaque JSON; their
    // exact mediasoup-client shape is validated at runtime by the device.
    const transport = device.createRecvTransport(params as never);
    transport.on('connect', ({ dtlsParameters }, callback, errback) => {
      this.sfuRequest('voice:sfu:connect-transport', {
        transportId: transport.id,
        dtlsParameters: dtlsParameters as DtlsParameters,
      })
        .then(() => callback())
        .catch((err) => errback(err instanceof Error ? err : new Error('sfu_connect_failed')));
    });
    transport.observer.on('close', () => {
      if (this.sfuRecvTransport === transport) this.sfuRecvTransport = null;
    });
    transport.on('connectionstatechange', (state) => this.onSfuTransportState(transport, 'recv', state));
    this.sfuRecvTransport = transport;
    this.host.recordDebug(`sfu:recv-transport:${transport.id}`);
    return transport;
  }

  private async consumeSfuProducer(channelId: string, producer: SfuProducerSummary): Promise<void> {
    if (producer.userId === this.host.getMyUserId()) return;
    if (this.sfuRemoteConsumers.has(producer.producerId)) return;
    if (this.sfuConsumingProducerIds.has(producer.producerId)) return;
    this.sfuConsumingProducerIds.add(producer.producerId);
    try {
    const device = await this.ensureSfuDevice(channelId);
    const recvTransport = await this.ensureSfuRecvTransport(channelId, device);
    const params = await this.sfuRequest<SfuConsumerParams>('voice:sfu:consume', {
      channelId,
      transportId: recvTransport.id,
      producerId: producer.producerId,
      rtpCapabilities: device.rtpCapabilities,
    });
    const consumer = await recvTransport.consume({
      id: params.id,
      producerId: params.producerId,
      kind: params.kind,
      rtpParameters: params.rtpParameters,
    });
    const stream = new MediaStream([consumer.track]);
    this.sfuRemoteConsumers.set(params.producerId, {
      consumer,
      stream,
      userId: params.userId,
      producerId: params.producerId,
      mediaTag: params.mediaTag ?? (params.kind === 'audio' ? 'microphone' : 'camera'),
    });
    consumer.observer.on('close', () => {
      this.sfuRemoteConsumers.delete(params.producerId);
      this.refreshSfuRemoteStreams();
    });
    await this.sfuRequest('voice:sfu:resume-consumer', { consumerId: consumer.id });
    this.host.recordDebug(`sfu:consume:${params.userId}:${params.producerId}`);
    this.refreshSfuRemoteStreams();
    } finally {
      this.sfuConsumingProducerIds.delete(producer.producerId);
    }
  }

  async refreshSfuProducers(channelId: string): Promise<void> {
    if (this.resumePending) return;
    // Consumers we had before asking: any the server no longer lists belong to
    // producers that closed while we weren't listening (a reconnect). Ones
    // added while the request was out are newer than the answer — left alone.
    const before = [...this.sfuRemoteConsumers.keys()];
    const existing = await this.sfuRequest<{ producers: SfuProducerSummary[] }>(
      'voice:sfu:list-producers',
      { channelId },
    );
    const listed = new Set(existing.producers.map((producer) => producer.producerId));
    for (const producerId of before) {
      if (!listed.has(producerId)) this.closeSfuConsumer(producerId);
    }
    await Promise.all(existing.producers.map((producer) => this.consumeSfuProducer(channelId, producer)));
  }

  /** A media session the server could keep across a reconnect. */
  hasSession(): boolean {
    return this.sessionSocketId !== null && (this.sfuSendTransport !== null || this.sfuRecvTransport !== null);
  }

  /** The socket is back and about to rejoin: hold the session until the
   *  answer. Returns whether there is a session to ask the server to keep. */
  beginResume(): boolean {
    this.resumePending = this.hasSession();
    this.syncReconnecting();
    return this.resumePending;
  }

  /**
   * The rejoin answered. Kept: the session now belongs to the new socket — a
   * transport that lost its path meanwhile restarts ICE, producers that came
   * and went while we were away are caught up. Not kept: rebuild on the new
   * socket, as after any reconnect.
   */
  finishResume(resumed: boolean) {
    if (!this.resumePending) return;
    this.resumePending = false;
    const channelId = this.host.getActiveChannelId();
    const socketId = getSocket().id ?? null;
    if (!channelId || !this.host.isMediaReady()) {
      this.syncReconnecting();
      return;
    }
    if (resumed && socketId && this.hasSession()) {
      this.host.recordDebug(`sfu:resumed:${this.sessionSocketId}->${socketId}`);
      this.sessionSocketId = socketId;
      for (const [transport, direction] of [
        [this.sfuSendTransport, 'send'],
        [this.sfuRecvTransport, 'recv'],
      ] as const) {
        if (!transport || transport.closed || isConnected(transport)) continue;
        // A fresh cycle: whatever ran before the drop could not reach the
        // server, and its answers (if any come) belong to an ended generation.
        let entry = this.sfuIceRecovery.get(transport.id);
        if (!entry) {
          entry = { phase: 'idle', timer: null, restartedAt: 0, gen: 0 };
          this.sfuIceRecovery.set(transport.id, entry);
        }
        this.endIceCycle(entry);
        entry.restartedAt = 0;
        void this.restartSfuIce(transport, direction, entry);
      }
      void this.refreshSfuProducers(channelId).catch((err) => {
        this.host.setError(err instanceof Error ? err.message : 'sfu_refresh_failed');
      });
      this.syncReconnecting();
      return;
    }
    this.syncReconnecting();
    void this.startSfu(channelId)
      .then(() => this.refreshSfuProducers(channelId))
      .catch((err) => {
        this.host.setError(err instanceof Error ? err.message : 'sfu_start_failed');
      });
  }

  /** Requests about our transports make sense only on the socket the server
   *  knows the session by — not while it is down or a rejoin is pending. */
  private onSessionSocket(): boolean {
    return !this.resumePending && (getSocket().id ?? null) === this.sessionSocketId;
  }

  private syncReconnecting() {
    const busy =
      this.resumePending ||
      this.sfuReconnectTimer !== null ||
      [...this.sfuIceRecovery.values()].some((e) => e.phase === 'restarting' || e.phase === 'checking');
    if (busy === this.reconnecting) return;
    this.reconnecting = busy;
    this.host.setReconnecting(busy);
  }

  private bindSfuSignals() {
    if (this.sfuSignalBound) return;
    this.sfuSignalBound = true;
    getSocket().on('voice:sfu:producer:add', (producer: SfuProducerSummary & { channelId?: string }) => {
      if (!this.host.isMediaReady()) return;
      const channelId = this.host.getActiveChannelId();
      if (!channelId || producer.channelId !== channelId) return;
      void this.consumeSfuProducer(channelId, producer).catch((err) => {
        this.host.setError(err instanceof Error ? err.message : 'sfu_consume_failed');
      });
    });
    getSocket().on('voice:sfu:producer:remove', (producer: { channelId?: string; producerId?: string }) => {
      if (!this.host.isMediaReady()) return;
      if (!producer.producerId || producer.channelId !== this.host.getActiveChannelId()) return;
      this.closeSfuConsumer(producer.producerId);
    });
  }

  private sfuRequest<T extends object = Record<string, never>>(
    event: string,
    payload: Record<string, unknown>,
  ): Promise<T> {
    const socket = getSocket();
    return new Promise((resolve, reject) => {
      socket.timeout(10_000).emit(event, payload, (err: Error | null, result?: unknown) => {
        if (err) {
          reject(new Error('sfu_timeout'));
          return;
        }
        const response = result as ({ ok: true } & T) | { ok: false; error?: string } | undefined;
        if (!response?.ok) {
          reject(new Error(response?.error ?? 'sfu_error'));
          return;
        }
        resolve(response as T);
      });
    });
  }

  private refreshSfuRemoteStreams() {
    this.host.setRemoteStreams(
      [...this.sfuRemoteConsumers.values()]
        .map((entry) => ({ userId: entry.userId, stream: entry.stream, mediaTag: entry.mediaTag }))
        .filter((entry) => entry.stream.getTracks().some((track) => track.readyState === 'live')),
    );
  }

  private closeSfuConsumer(producerId: string) {
    const entry = this.sfuRemoteConsumers.get(producerId);
    if (!entry) return;
    entry.consumer.close();
    this.sfuRemoteConsumers.delete(producerId);
    this.host.recordDebug(`sfu:consumer-close:${producerId}`);
    this.refreshSfuRemoteStreams();
  }

  closeSfu(options: { clearReconnect?: boolean } = {}) {
    const clearReconnect = options.clearReconnect ?? true;
    this.closingSfu = true;
    if (clearReconnect && this.sfuReconnectTimer !== null) {
      window.clearTimeout(this.sfuReconnectTimer);
      this.sfuReconnectTimer = null;
      this.sfuReconnectAttempts = 0;
    }
    this.sfuStartPromise = null;
    this.sfuRecvTransportPromise = null;
    this.sfuConsumingProducerIds.clear();
    this.clearSfuIceRecovery();
    this.sfuAudioProducer?.close();
    this.sfuAudioProducer = null;
    this.sfuVideoProducer?.close();
    this.sfuVideoProducer = null;
    this.sfuScreenProducer?.close();
    this.sfuScreenProducer = null;
    this.sfuScreenAudioProducer?.close();
    this.sfuScreenAudioProducer = null;
    for (const entry of this.sfuRemoteConsumers.values()) {
      entry.consumer.close();
    }
    this.sfuRemoteConsumers.clear();
    this.sfuSendTransport?.close();
    this.sfuRecvTransport?.close();
    this.sfuSendTransport = null;
    this.sfuRecvTransport = null;
    this.sfuDevice = null;
    this.sessionSocketId = null;
    this.resumePending = false;
    this.closingSfu = false;
    this.refreshSfuRemoteStreams();
    this.syncReconnecting();
  }

  /**
   * A transport lost its network path. «disconnected» often heals by itself
   * (a Wi-Fi hiccup), so it gets ICE_GRACE_MS; still down, or «failed» → ICE
   * restarts on the same transport, keeping producers and consumers — the
   * screen share and camera included. Only if that does not bring it back is
   * the whole session rebuilt, which restores the microphone alone. It used to
   * rebuild on the first «disconnected»: a two-second blip ended a screen share.
   */
  private onSfuTransportState(transport: Transport, direction: 'send' | 'recv', state: string) {
    this.host.recordDebug(`sfu:${direction}-state:${state}`);
    let entry = this.sfuIceRecovery.get(transport.id);
    if (state === 'connected' || state === 'closed') {
      if (entry) this.endIceCycle(entry);
      if (state === 'closed') this.sfuIceRecovery.delete(transport.id);
      return;
    }
    if (state !== 'disconnected' && state !== 'failed') return;
    if (!entry) {
      entry = { phase: 'idle', timer: null, restartedAt: 0, gen: 0 };
      this.sfuIceRecovery.set(transport.id, entry);
    }
    // A restart is in flight or being checked: its own outcome decides.
    if (entry.phase === 'restarting' || entry.phase === 'checking') return;
    if (entry.phase === 'grace') {
      if (state !== 'failed') return;
      this.endIceCycle(entry); // failed: no point waiting out the grace
    }
    const recovery = entry;
    if (state === 'failed') {
      void this.restartSfuIce(transport, direction, recovery);
      return;
    }
    recovery.phase = 'grace';
    recovery.timer = window.setTimeout(() => {
      recovery.timer = null;
      recovery.phase = 'idle';
      if (isConnected(transport)) return;
      void this.restartSfuIce(transport, direction, recovery);
    }, ICE_GRACE_MS);
  }

  private endIceCycle(entry: IceRecovery) {
    if (entry.timer !== null) window.clearTimeout(entry.timer);
    entry.timer = null;
    entry.phase = 'idle';
    entry.gen += 1;
    this.syncReconnecting();
  }

  /** Still the recovery of a live transport of this session? A late answer
   *  after leaving, a channel switch or a rebuild must not touch what's there now. */
  private iceCycleCurrent(transport: Transport, entry: IceRecovery): boolean {
    return !transport.closed && this.sfuIceRecovery.get(transport.id) === entry;
  }

  private async restartSfuIce(transport: Transport, direction: 'send' | 'recv', entry: IceRecovery) {
    if (!this.iceCycleCurrent(transport, entry) || isConnected(transport)) return;
    // The socket is down or a rejoin is pending: finishResume restarts it.
    // Checked first — a rebuild now would throw away the very session the
    // server is holding for us.
    if (!this.onSessionSocket()) return;
    // Restarted a moment ago and down again: the path doesn't hold — rebuild.
    if (entry.restartedAt && Date.now() - entry.restartedAt < ICE_RETRY_MS) {
      this.scheduleSfuReconnect(`${direction}:ice-restart-did-not-help`);
      return;
    }
    entry.phase = 'restarting';
    entry.restartedAt = Date.now();
    const gen = ++entry.gen;
    // Healed («connected»), closed, or a newer cycle started while we waited.
    const stillOurs = () => this.iceCycleCurrent(transport, entry) && entry.gen === gen;
    this.syncReconnecting();
    try {
      const { iceParameters } = await this.sfuRequest<{ iceParameters: unknown }>('voice:sfu:restart-ice', {
        transportId: transport.id,
      });
      if (!stillOurs()) return;
      // mediasoup IceParameters from the SFU, opaque JSON to us.
      await transport.restartIce({ iceParameters: iceParameters as never });
      if (!stillOurs()) return;
      this.host.recordDebug(`sfu:${direction}:ice-restart`);
      if (isConnected(transport)) {
        this.endIceCycle(entry);
        return;
      }
      entry.phase = 'checking';
      entry.timer = window.setTimeout(() => {
        entry.timer = null;
        if (!stillOurs()) return;
        entry.phase = 'idle';
        this.syncReconnecting();
        // The socket dropped meanwhile: the rejoin decides (finishResume).
        if (!isConnected(transport) && this.onSessionSocket()) {
          this.scheduleSfuReconnect(`${direction}:ice-restart-timeout`);
        }
      }, ICE_CHECK_MS);
    } catch {
      if (!stillOurs()) return;
      entry.phase = 'idle';
      this.syncReconnecting();
      // The socket dropped with the request out: the rejoin decides.
      if (!this.onSessionSocket()) return;
      // The server no longer knows the transport or refused: rebuild.
      this.scheduleSfuReconnect(`${direction}:ice-restart-failed`);
    }
  }

  private clearSfuIceRecovery() {
    for (const entry of this.sfuIceRecovery.values()) {
      if (entry.timer !== null) window.clearTimeout(entry.timer);
    }
    this.sfuIceRecovery.clear();
  }

  private scheduleSfuReconnect(reason: string) {
    if (this.closingSfu || !this.host.isMediaReady()) return;
    const channelId = this.host.getActiveChannelId();
    if (!channelId || !this.host.getMyUserId()) return;
    if (this.sfuReconnectTimer !== null) return;
    const delay = Math.min(8_000, 1_000 * 2 ** this.sfuReconnectAttempts);
    this.sfuReconnectAttempts += 1;
    this.host.recordDebug(`sfu:reconnect:${reason}:delay=${delay}`);
    this.closeSfu({ clearReconnect: false });
    this.sfuReconnectTimer = window.setTimeout(() => {
      this.sfuReconnectTimer = null;
      if (this.host.getActiveChannelId() !== channelId || !this.host.isMediaReady()) {
        this.syncReconnecting();
        return;
      }
      void this.startSfu(channelId)
        .then(() => this.refreshSfuProducers(channelId))
        .then(() => {
          this.sfuReconnectAttempts = 0;
          this.host.setError(null);
          this.host.recordDebug('sfu:reconnect:ok');
          this.syncReconnecting();
        })
        .catch((err) => {
          this.host.setError(err instanceof Error ? err.message : 'sfu_reconnect_failed');
          this.scheduleSfuReconnect('retry');
        });
    }, delay);
    this.syncReconnecting();
  }

  async closeSfuVideoProducer(channelId: string | null) {
    await this.closeSfuProducer('video', channelId);
  }

  async closeSfuScreenProducer(channelId: string | null) {
    await this.closeSfuProducer('screen', channelId);
  }

  async closeSfuScreenAudioProducer(channelId: string | null) {
    await this.closeSfuProducer('screen-audio', channelId);
  }

  /**
   * Shared body for video + screen producer teardown — both were
   * byte-identical save for the producer field. `kind` selects which
   * slot to drain and tags the debug events.
   */
  private async closeSfuProducer(
    kind: 'video' | 'screen' | 'screen-audio',
    channelId: string | null,
  ) {
    const field =
      kind === 'video'
        ? 'sfuVideoProducer'
        : kind === 'screen'
          ? 'sfuScreenProducer'
          : 'sfuScreenAudioProducer';
    const producer = this[field];
    if (!producer) return;
    this[field] = null;
    producer.close();
    if (channelId) {
      await this.sfuRequest('voice:sfu:close-producer', {
        channelId,
        producerId: producer.id,
      }).catch((err) => {
        this.host.recordDebug(
          `sfu:close-${kind}:error:${err instanceof Error ? err.message : 'unknown'}`,
        );
      });
    }
    this.host.recordDebug(`sfu:close-${kind}:${producer.id}`);
  }

  /** Hot-swap the live mic track on the SFU audio producer (device switch /
   *  filter change). No-op when not producing. */
  async replaceAudioTrack(track: MediaStreamTrack): Promise<void> {
    if (!this.sfuAudioProducer) return;
    await this.sfuAudioProducer.replaceTrack({ track }).catch((err) => {
      this.host.recordDebug(`audio:swap:sfu:error:${err instanceof Error ? err.name : 'unknown'}`);
    });
  }

  /** Apply the effective transmit state to the SFU audio producer — pause when
   *  muted/gated, resume otherwise. Pausing (vs disabling the track) keeps the
   *  local VAD analyser reading the mic. */
  setTransmitting(transmit: boolean) {
    const producer = this.sfuAudioProducer;
    if (!producer) return;
    if (transmit && producer.paused) producer.resume();
    else if (!transmit && !producer.paused) producer.pause();
  }

  /** Re-produce the mic on the existing send transport after a permission
   *  retry, when already in an SFU channel and not yet producing. */
  async retryAudioProduce(channelId: string, audioTrack: MediaStreamTrack): Promise<void> {
    const device = await this.ensureSfuDevice(channelId);
    const sendTransport = await this.ensureSfuSendTransport(channelId, device);
    if (!this.sfuAudioProducer && device.canProduce('audio')) {
      this.sfuAudioProducer = await sendTransport.produce({
        track: audioTrack,
        codecOptions: {
          opusStereo: this.host.getAudioProcessingMode() === 'studio',
          opusFec: true,
          opusDtx: this.host.getAudioProcessingMode() !== 'studio',
        },
        appData: { mediaTag: 'microphone' },
      });
      this.sfuAudioProducer.observer.on('close', () => {
        this.sfuAudioProducer = null;
      });
      this.host.recordDebug(`sfu:retry-produce:${this.sfuAudioProducer.id}`);
    }
  }

  /** Live remote consumer count — for the debug snapshot. */
  remoteConsumerCount(): number {
    return this.sfuRemoteConsumers.size;
  }

  async diagnostics(): Promise<VoiceDiagnostics> {
    const transports = [this.sfuSendTransport, this.sfuRecvTransport].filter(
      (transport): transport is Transport => Boolean(transport),
    );
    const reports = await Promise.all(transports.map((transport) => transport.getStats()));
    const states = transports.map((transport) => transport.connectionState);
    const state = states.includes('failed')
      ? 'failed'
      : states.includes('disconnected') || transports.length === 0
        ? 'disconnected'
        : states.every((value) => value === 'connected')
          ? 'connected'
          : 'connecting';
    return { backend: 'sfu', state, ...summarizeRtcStats(reports) };
  }
}
